// SolveQuadraticWithoutBTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { toTextFractionLatex } from "../../../../utils/fractionLatex";

// === UTIL ===
function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  for (let d = 1; d <= 10; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;
      return intPart === 0
        ? `${sign}\\tfrac{${num}}{${den}}`
        : `${sign}${intPart}\\tfrac{${num}}{${den}}`;
    }
  }

  return `${sign}${abs.toFixed(2)}`;
}

// === GENERATE TASK ===
export function generateTask() {
  let a, c, x1, x2;

  do {
    a = Math.floor(Math.random() * 5 + 1) * (Math.random() < 0.5 ? -1 : 1);
    const root = Math.floor(Math.random() * 5 + 1);
    x1 = root;
    x2 = -root;
    c = -a * root * root;
  } while (a === 0 || c === 0);

  const latex = `${a !== 1 ? (a === -1 ? "-" : a) : ""}x^2 ${c > 0 ? "+ " + c : "- " + Math.abs(c)} = 0`;

  return {
    type: "SolveQuadraticWithoutBTask",
    data: { a, c, x1, x2, latex }
  };
}

// === RENDER TASK ===
export function renderTask({ data }) {
  return <p>Rozwiąż równanie: <InlineMath math={data.latex} /></p>;
}

// === INPUT ===
export function renderInput(_, userInput, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_1 ='} /></span>
        <FractionInput value={userInput?.x1 || ""} onChange={val => onChange({ ...userInput, x1: val })} disabled={disabled} label="x₁" />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math={'x_2 ='} /></span>
        <FractionInput value={userInput?.x2 || ""} onChange={val => onChange({ ...userInput, x2: val })} disabled={disabled} label="x₂" />
      </div>
    </div>
  );
}

// === CHECK ANSWER ===
export function checkAnswer({ data }, userInput) {
  const { x1, x2 } = data;
  const u1 = userInput?.x1?.parsed;
  const u2 = userInput?.x2?.parsed;

  const correct =
    (Math.abs(u1 - x1) < 0.01 && Math.abs(u2 - x2) < 0.01) ||
    (Math.abs(u1 - x2) < 0.01 && Math.abs(u2 - x1) < 0.01);

  return { correct, expected: [x1, x2] };
}

// === RENDER ANSWER ===
// Krótka wersja – do lewej.
export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <div><InlineMath math={`x_1 = ${toTextFractionLatex(formatFractionLatex(data.x1))}`} /></div>
      <div><InlineMath math={`x_2 = ${toTextFractionLatex(formatFractionLatex(data.x2))}`} /></div>
    </div>
  );
}

// === RENDER EXPLANATION ===
// Wyniki w nowej linijce z przepisaną lewą stroną i końcowa odpowiedź wyśrodkowana.
export function renderExplanation({ data }) {
  const { a, c, x1, x2 } = data;
  const rightSide = -c;
  const divided = -c / a;
  const sqrt = Math.sqrt(divided);

  return (
    <div className="space-y-3">
      <p><strong>Krok 1:</strong> Przenosimy wyraz wolny na drugą stronę:</p>
      <BlockMath math={`${a}x^2 = ${rightSide}`} />

      <p><strong>Krok 2:</strong> Dzielimy przez {a}:</p>
      <BlockMath
        math={`\\begin{aligned}
x^2 &= \\dfrac{${rightSide}}{${a}} \\\\
x^2 &= ${divided}
\\end{aligned}`}
      />

      <p><strong>Krok 3:</strong> Wyciągamy pierwiastek:</p>
      <BlockMath
        math={`\\begin{aligned}
x &= \\pm\\sqrt{${divided}} \\\\
x &= \\pm ${formatFractionLatex(sqrt)}
\\end{aligned}`}
      />

      <p><strong>Odpowiedź końcowa:</strong></p>
      <div style={{ textAlign: "center", lineHeight: 1.6 }}>
        <BlockMath math={`x_1 = ${formatFractionLatex(x1)},\\quad x_2 = ${formatFractionLatex(x2)}`} />
      </div>
    </div>
  );
}

// === RENDER USER ANSWER ===
export function renderUserAnswer(userInput) {
  if (
    !userInput ||
    typeof userInput !== "object" ||
    !userInput.x1?.raw ||
    !userInput.x2?.raw
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const x1 = formatLatexPreview(userInput.x1.raw, "x_1");
  const x2 = formatLatexPreview(userInput.x2.raw, "x_2");

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div><InlineMath math={x1} /></div>
      <div style={{ marginTop: "0.5rem" }}>
        <InlineMath math={x2} />
      </div>
    </div>
  );
}
